import type { SaleRules } from "./model";

/**
 * Unidades de MEDIDA (peso/volume/comprimento): a quantidade pode ser
 * fracionária, no passo configurado. Mesma lista que o card do design system
 * usa para decidir o modo "weight" do stepper. O nome da unidade é só UM dos
 * sinais (ver `isFractionalSale`): o cadastro grava "CX" numa banana vendida
 * por quilo, e o passo 0,16 é quem diz que ela é fracionária.
 */
const MEASURE_UNITS = new Set(["kg", "g", "gr", "l", "lt", "ml", "m", "mt", "cm", "m2", "m3"]);

/** Peso vai com até 2 casas (regra da nest para produtos pesáveis). */
const SCALE = 100;

export function isMeasureUnit(unitType: string | undefined): boolean {
    return MEASURE_UNITS.has((unitType ?? "").trim().toLowerCase());
}

/**
 * Quantidade pode ser fracionária? Unidade de medida OU passo/mínimo com
 * casas decimais — a MESMA leitura da nest (que só olha mínimo e incremento,
 * sem depender do nome da unidade) e do `isWeightSale` do card.
 *
 * Decidir só pelo nome arredondava 0,16 para 1 na banana cadastrada como
 * "CX": a nest então recusava com "deve ser em múltiplos de 0,16".
 */
export function isFractionalSale(rules: Pick<SaleRules, "unitType" | "incrementStep" | "minPurchase">): boolean {
    if (isMeasureUnit(rules.unitType)) return true;
    const step = rules.incrementStep > 0 ? rules.incrementStep : 1;
    const min = rules.minPurchase > 0 ? rules.minPurchase : step;
    return !Number.isInteger(step) || !Number.isInteger(min);
}

/**
 * Ajusta uma quantidade às regras do produto ANTES de ir para a API:
 *
 * - abaixo do piso (`minPurchase`) sobe para o piso;
 * - fora do passo (`incrementStep`) vai para o múltiplo mais próximo;
 * - acima do teto (`maxPurchase`) desce para o teto;
 * - produto unitário sai inteiro; pesável sai com 2 casas.
 *
 * `0` (ou menos) é devolvido como `0`: significa "remover", nunca se ajusta
 * para cima. A nest valida as mesmas regras e responde 400 com a mensagem
 * ("A quantidade mínima deste produto é X" / "deve ser em múltiplos de X");
 * ajustar aqui evita a ida e volta e a mensagem para um valor que o próprio
 * stepper deixou digitar.
 */
/** Rótulo da unidade de MEDIDA como o cliente lê: "kg", "L", "m²". Fora da lista, o texto do cadastro. */
const MEASURE_LABEL: Record<string, string> = {
    kg: "kg", g: "g", gr: "g",
    l: "L", lt: "L", ml: "ml",
    m: "m", mt: "m", cm: "cm", m2: "m²", m3: "m³",
};

/**
 * Unidade exibida ao lado do preço ("R$ 2,99 / kg").
 *
 * Pesável com unidade de cadastro que não é medida ("CX" na banana por quilo)
 * sai como "kg": é a base da quantidade fracionária, a mesma que o stepper
 * usa para mostrar "160 g". Produto de contagem mantém o texto do cadastro.
 */
export function saleUnitLabel(rules: SaleRules): string {
    const unit = (rules.unitType ?? "").trim().toLowerCase();
    if (MEASURE_LABEL[unit]) return MEASURE_LABEL[unit];
    return isFractionalSale(rules) ? "kg" : rules.unitType;
}

/** pt-BR, até 3 casas: 1 → "1"; 0,5 → "0,5"; 1,25 → "1,25". */
const formatNumber = (value: number) => value.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
/** Mesmo arredondamento do stepper — sem 160,00000000000003. */
const scale = (value: number, factor: number) => Math.round(value * factor * 1000) / 1000;

/**
 * Quantidade pesável como MEDIDA legível: 0,16 → "160 g", 1,5 → "1,5 kg",
 * 0,5 L → "500 ml". Base quilo para qualquer unidade que não seja volume ou
 * comprimento — inclusive "CX"/"UN" com passo fracionário.
 *
 * Cópia da `formatSaleQuantity` do `/core` do big2be-ui (mesmas regras e
 * testes). Fica aqui até o entrypoint `/core` da lib estar publicado e aliasado
 * no `dev:ui`; aí este helper vira um re-export.
 */
export function formatSaleQuantity(quantity: number, unitType?: string): string {
    const value = Number.isFinite(quantity) ? quantity : 0;
    const unit = unitType?.trim().toLowerCase() ?? "";

    switch (unit) {
        case "g":
        case "gr":
            return value >= 1000 ? `${formatNumber(scale(value, 1 / 1000))} kg` : `${formatNumber(value)} g`;
        case "ml":
            return value >= 1000 ? `${formatNumber(scale(value, 1 / 1000))} L` : `${formatNumber(value)} ml`;
        case "l":
        case "lt":
            return value < 1 ? `${formatNumber(scale(value, 1000))} ml` : `${formatNumber(value)} L`;
        case "cm":
            return value >= 100 ? `${formatNumber(scale(value, 1 / 100))} m` : `${formatNumber(value)} cm`;
        case "m":
        case "mt":
            return value < 1 ? `${formatNumber(scale(value, 100))} cm` : `${formatNumber(value)} m`;
        case "m2":
            return `${formatNumber(value)} m²`;
        case "m3":
            return `${formatNumber(value)} m³`;
        default:
            return value < 1 ? `${formatNumber(scale(value, 1000))} g` : `${formatNumber(value)} kg`;
    }
}

/**
 * Frase que explica o stepper de um pesável — "Vendido por peso, em porções de
 * 160 g" — com o mínimo quando ele difere do passo ("mínimo de 500 g").
 * Hoje o cliente vê a quantidade andar de 0,16 em 0,16 sem saber por quê.
 * `null` para produto de contagem.
 */
export function purchaseStepHint(rules: SaleRules): string | null {
    if (!isFractionalSale(rules)) return null;
    const step = rules.incrementStep > 0 ? rules.incrementStep : 1;
    const min = rules.minPurchase > 0 ? rules.minPurchase : step;
    const base = `Vendido por peso, em porções de ${formatSaleQuantity(step, rules.unitType)}`;
    return min !== step ? `${base} (mínimo de ${formatSaleQuantity(min, rules.unitType)})` : base;
}

/**
 * Próxima quantidade ao clicar −/+ no carrinho: anda no passo do produto e
 * devolve `0` (remover) quando o "−" cai abaixo do piso — como o card faz.
 * Sem regras (ainda carregando), ±1, que é o comportamento de contagem.
 */
export function stepQuantity(quantity: number, direction: 1 | -1, rules?: SaleRules): number {
    if (!rules) return Math.max(0, quantity + direction);
    const step = rules.incrementStep > 0 ? rules.incrementStep : 1;
    // Piso é o mínimo, não `max(mínimo, passo)`: mínimo 0,1 e passo 0,5 = 0,1 → 0,6 → 1,1 (a nest conta do mínimo).
    const floor = rules.minPurchase > 0 ? rules.minPurchase : step;
    const next = Math.round((quantity + direction * step) * 1000) / 1000;
    return next < floor ? 0 : snapQuantity(next, rules);
}

export function snapQuantity(value: number, rules: SaleRules): number {
    if (!Number.isFinite(value) || value <= 0) return 0;

    const measure = isFractionalSale(rules);
    const step = rules.incrementStep > 0 ? rules.incrementStep : 1;
    const min = rules.minPurchase > 0 ? rules.minPurchase : step;
    const max = rules.maxPurchase > 0 ? rules.maxPurchase : Number.MAX_SAFE_INTEGER;

    // Em inteiros escalados: `0.1 + 0.2` não cai certo em ponto flutuante.
    const scaledStep = Math.round(step * SCALE);
    const scaledMin = Math.round(min * SCALE);
    const scaledValue = Math.round(value * SCALE);

    // Múltiplo do passo mais próximo, contado a partir do piso.
    const steps = Math.max(0, Math.round((scaledValue - scaledMin) / scaledStep));
    let snapped = (scaledMin + steps * scaledStep) / SCALE;

    if (!measure) snapped = Math.max(1, Math.round(snapped));
    snapped = Math.min(snapped, max);
    if (snapped < min) snapped = min;

    return Number(snapped.toFixed(measure ? 2 : 0));
}
